import { Reference } from '@approvalmax/types';
import {
    CheckboxEditor,
    DateTimeEditor,
    DropdownEditor,
    NumberEditor,
    TextAreaEditor,
    TextEditor,
    TimeEditor,
} from '@approvalmax/ui';
import { RichEditor } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { memo, useMemo } from 'react';
import { ocrIsLoadingState } from 'shared/states';

import { getNetSuiteContext } from '../../../../selectors/netsuite/netSuiteSelectors';
import { getCustomFieldIsInvalid, getCustomFieldMaxLength, sortCustomFields } from '../../helpers';
import { useCustomFields } from '../../hooks';
import { netSuiteCustomFieldsState } from '../../resources/states';
import NetSuiteCustomReferenceDataProvider from '../NetSuiteCustomReferenceDataProvider/NetSuiteCustomReferenceDataProvider';
import { StyledCustomField } from './NetSuiteCustomFields.styles';
import { NetSuiteCustomFieldsProps } from './NetSuiteCustomFields.types';

export const NetSuiteCustomFields = memo<NetSuiteCustomFieldsProps>((props) => {
    const { workflowId, showErrors, noFieldMargins } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);

    const { customFields } = netSuiteEditingContext;

    const { onCustomFieldChange } = useCustomFields(customFields);

    const availableCustomFields = useMemo(() => {
        const filteredFields = customFields.filter((field) => {
            const isAppropriateLevel =
                field.netSuiteField?.level === 'Header' || field.netSuiteField?.level === 'Header And Lines';

            return (
                !field.isArchived &&
                isAppropriateLevel &&
                field.workflows?.some((workflow) => workflow.workflowId === workflowId && workflow.isActive)
            );
        });

        return sortCustomFields(filteredFields);
    }, [customFields, workflowId]);

    return (
        <>
            {availableCustomFields.map((field) => {
                const fieldWorkflowSettings = field.workflows?.find((workflow) => workflow.workflowId === workflowId);

                const isEditable = fieldWorkflowSettings?.isEditable ?? false;

                const isMandatory = fieldWorkflowSettings?.isMandatory ?? false;

                const disabled = !isEditable || ocrIsLoading;

                const fieldData = customFieldsValues[field.id];

                const isFieldInvalid = showErrors && getCustomFieldIsInvalid(field, fieldData, workflowId);

                switch (field.netSuiteField?.type) {
                    case 'CheckBox':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <CheckboxEditor
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueBoolean', value)}
                                    value={customFieldsValues[field.id]?.valueBoolean || false}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'Currency':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NumberEditor
                                    allowFloat
                                    value={customFieldsValues[field.id]?.valueDecimal || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDecimal', value)}
                                    placeholder=' '
                                    displayFormat='currency'
                                    max={constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    min={-constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    precision={2}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'DecimalNumber':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NumberEditor
                                    allowFloat
                                    value={customFieldsValues[field.id]?.valueDecimal || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDecimal', value)}
                                    placeholder=' '
                                    displayFormat='as-is'
                                    max={constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    min={-constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    precision={8}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'IntegerNumber':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NumberEditor
                                    allowFloat={false}
                                    value={customFieldsValues[field.id]?.valueLong || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueLong', value)}
                                    placeholder=' '
                                    displayFormat='as-is'
                                    max={constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    min={-constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'Percent':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NumberEditor
                                    allowFloat
                                    value={customFieldsValues[field.id]?.valueDecimal || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDecimal', value)}
                                    placeholder=' '
                                    displayFormat='as-is'
                                    max={100}
                                    min={0}
                                    precision={4}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'Date':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <DateTimeEditor
                                    value={customFieldsValues[field.id]?.valueDate || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDate', value)}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'TimeOfDay':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <TimeEditor
                                    variant='time'
                                    value={customFieldsValues[field.id]?.valueDate || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDate', value)}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'Datetime':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <TimeEditor
                                    variant='datetime'
                                    value={customFieldsValues[field.id]?.valueDate || null}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueDate', value)}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'Hyperlink':
                    case 'PhoneNumber':
                    case 'EMailAddress': {
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <TextEditor
                                    value={fieldData?.valueString || ''}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueString', value)}
                                    placeholder=''
                                    changeOnBlur
                                    maxLength={getCustomFieldMaxLength(field)}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );
                    }

                    case 'TextArea':
                    case 'LongText':
                    case 'FreeFormText': {
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <TextAreaEditor
                                    value={customFieldsValues[field.id]?.valueString || ''}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueString', value)}
                                    changeOnBlur
                                    minHeight={5}
                                    maxLength={getCustomFieldMaxLength(field)}
                                    disabled={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );
                    }

                    case 'RichText':
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <RichEditor
                                    value={customFieldsValues[field.id]?.valueString || ''}
                                    onChange={(value) => onCustomFieldChange(field.id, 'valueString', value)}
                                    readOnly={disabled}
                                    invalid={isFieldInvalid}
                                />
                            </StyledCustomField>
                        );

                    case 'ListRecord': {
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NetSuiteCustomReferenceDataProvider fieldId={field.id}>
                                    <DropdownEditor
                                        value={customFieldsValues[field.id]?.valueRef || null}
                                        singleLinePerItem={false}
                                        onChange={(value: Reference | null) =>
                                            onCustomFieldChange(field.id, 'valueRef', value)
                                        }
                                        disabled={disabled}
                                        invalid={isFieldInvalid}
                                    />
                                </NetSuiteCustomReferenceDataProvider>
                            </StyledCustomField>
                        );
                    }

                    case 'MultipleSelect': {
                        return (
                            <StyledCustomField
                                key={field.id}
                                title={field.name}
                                required={isMandatory}
                                $hasMargins={!noFieldMargins}
                            >
                                <NetSuiteCustomReferenceDataProvider fieldId={field.id}>
                                    <DropdownEditor
                                        value={customFieldsValues[field.id]?.valueRefs || null}
                                        singleLinePerItem={false}
                                        multiple
                                        onChange={(value: Reference[] | null) =>
                                            onCustomFieldChange(field.id, 'valueRefs', value)
                                        }
                                        disabled={disabled}
                                        invalid={isFieldInvalid}
                                    />
                                </NetSuiteCustomReferenceDataProvider>
                            </StyledCustomField>
                        );
                    }

                    default:
                        return null;
                }
            })}
        </>
    );
});

NetSuiteCustomFields.displayName = 'NetSuiteCustomFields';
